import React, { useEffect, useRef, useState } from 'react';
import { api, downloadApiFile, uploadWithFormData } from '../api';
import { useBankConfig } from '../bank-config';

const EMPTY_FORM = {
  projectExternalId: '',
  tag: '',
  bankCheckEnabled: true,
  uploadMode: 'standard',
  hasHeader: true,
  file: null
};

const FILES_PAGE_SIZE = 10;
const CONTACTS_PAGE_SIZE = 100;
const STATUS_LABELS = {
  skorozvon_auth_error: '401 Skorozvon auth'
};

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

function renderStatusLabel(status) {
  return STATUS_LABELS[status] || status || '-';
}

export function FilesPage() {
  const bankConfig = useBankConfig();
  const { isTochka } = bankConfig;
  const [files, setFiles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [summary, setSummary] = useState(null);
  const [filesSearch, setFilesSearch] = useState('');
  const [appliedFilesSearch, setAppliedFilesSearch] = useState('');
  const [filesPage, setFilesPage] = useState(1);
  const [filesTotal, setFilesTotal] = useState(0);
  const [filesTotalPages, setFilesTotalPages] = useState(1);
  const [contactsSearch, setContactsSearch] = useState('');
  const [appliedContactsSearch, setAppliedContactsSearch] = useState('');
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsTotalPages, setContactsTotalPages] = useState(1);
  const [dragActive, setDragActive] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const pollingRef = useRef(null);
  useEffect(() => {
    if (isTochka) setForm((prev) => ({ ...prev, bankCheckEnabled: false, uploadMode: 'standard' }));
  }, [isTochka]);

  async function loadFiles(nextPage = filesPage, nextSearch = appliedFilesSearch, silent = false) {
    if (!silent) setLoading(true);
    try {
      const data = await api(
        `/files?page=${encodeURIComponent(nextPage)}&pageSize=${FILES_PAGE_SIZE}&search=${encodeURIComponent(nextSearch)}`
      );
      setFiles(Array.isArray(data?.items) ? data.items : []);
      setFilesTotal(Number(data?.total || 0));
      setFilesPage(Number(data?.page || nextPage || 1));
      setFilesTotalPages(Number(data?.totalPages || 1));
      setError('');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  async function loadSummary(fileId = selectedFileId, silent = false) {
    if (!fileId) {
      setSummary(null);
      return;
    }
    if (!silent) setSummaryLoading(true);
    try {
      const data = await api(`/files/${fileId}/summary`);
      setSummary(data || null);
    } finally {
      if (!silent) setSummaryLoading(false);
    }
  }

  async function loadContacts(fileId = selectedFileId, nextPage = contactsPage, nextSearch = appliedContactsSearch, nextStatus = selectedStatusFilter, silent = false) {
    if (!fileId) {
      setContacts([]);
      setContactsTotal(0);
      setContactsPage(1);
      setContactsTotalPages(1);
      return;
    }

    if (!silent) setContactsLoading(true);
    try {
      const data = await api(
        `/files/${fileId}/contacts?page=${encodeURIComponent(nextPage)}&pageSize=${CONTACTS_PAGE_SIZE}&search=${encodeURIComponent(nextSearch)}&status=${encodeURIComponent(nextStatus)}`
      );
      setContacts(Array.isArray(data?.items) ? data.items : []);
      setContactsTotal(Number(data?.total || 0));
      setContactsPage(Number(data?.page || nextPage || 1));
      setContactsTotalPages(Number(data?.totalPages || 1));
    } finally {
      if (!silent) setContactsLoading(false);
    }
  }

  function handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  }

  function handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  }

  function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const droppedFile = event.dataTransfer?.files?.[0] || null;
    if (droppedFile) {
      setForm((prev) => ({ ...prev, file: droppedFile }));
    }
  }

  useEffect(() => {
    loadFiles(1, appliedFilesSearch).catch((err) => setError(err.message || 'Не удалось загрузить файлы'));
  }, []);

  useEffect(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollingRef.current = setInterval(() => {
      if (document.hidden) return;
      loadFiles(filesPage, appliedFilesSearch, true).catch(() => {});
      if (selectedFileId) {
        loadSummary(selectedFileId, true).catch(() => {});
        loadContacts(selectedFileId, contactsPage, appliedContactsSearch, selectedStatusFilter, true).catch(() => {});
      }
    }, 3000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [filesPage, appliedFilesSearch, selectedFileId, contactsPage, appliedContactsSearch, selectedStatusFilter]);

  function setUploadMode(uploadMode) {
    setForm((prev) => ({
      ...prev,
      uploadMode,
      projectExternalId: uploadMode === 'bank_only' ? '' : prev.projectExternalId,
      tag: uploadMode === 'bank_only' ? '' : prev.tag,
      bankCheckEnabled: uploadMode === 'bank_only' ? true : prev.bankCheckEnabled
    }));
  }

  async function upload() {
    if (!form.file) {
      setError('Выберите файл для загрузки');
      return;
    }

    setUploading(true);
    setError('');
    setMessage('');
    setContacts([]);
    setSelectedFileId(null);
    setSummary(null);

    try {
      const fd = new FormData();
      fd.append('projectExternalId', form.uploadMode === 'bank_only' ? '' : form.projectExternalId);
      fd.append('tag', form.uploadMode === 'bank_only' ? '' : form.tag);
      fd.append('hasHeader', form.hasHeader ? 'true' : 'false');
      fd.append('bankCheckEnabled', String(form.uploadMode === 'bank_only' ? true : form.bankCheckEnabled));
      fd.append('uploadMode', form.uploadMode);
      fd.append('file', form.file);
      await uploadWithFormData('/files/upload', fd, { timeoutMs: 600000 });
      await loadFiles(1, appliedFilesSearch);
      setForm(EMPTY_FORM);
      setMessage('Файл загружен и поставлен в обработку');
    } finally {
      setUploading(false);
      setDragActive(false);
    }
  }

  async function showContacts(fileId) {
    setError('');
    setSelectedFileId(fileId);
    setContactsSearch('');
    setAppliedContactsSearch('');
    setSelectedStatusFilter('all');
    await Promise.all([
      loadSummary(fileId),
      loadContacts(fileId, 1, '', 'all')
    ]);
  }

  async function applyStatusFilter(status) {
    setSelectedStatusFilter(status);
    await loadContacts(selectedFileId, 1, appliedContactsSearch, status);
  }

  async function removeFile(fileId) {
    if (!window.confirm('Удалить файл и связанные контакты?')) {
      return;
    }

    setError('');
    setMessage('');
    await api(`/files/${fileId}`, { method: 'DELETE' });
    if (selectedFileId === fileId) {
      setSelectedFileId(null);
      setSummary(null);
      setContacts([]);
    }
    await loadFiles(filesPage, appliedFilesSearch);
    setMessage('Файл удалён');
  }


  async function exportFile(fileId) {
    setError('');
    await downloadApiFile(
      `/files/${fileId}/export`,
        `file_${fileId}_export.xls`,
      { timeoutMs: 120000 }
    );
    setMessage(`Выгрузка файла #${fileId} подготовлена`);
  }

  const statusEntries = Object.entries(summary?.stats?.byStatus || {}).sort((a, b) => Number(b[1]) - Number(a[1]));

  return (
    <div className="grid two-columns">
      {message ? <section className="card full-width success-banner">{message}</section> : null}
      {error ? <section className="card full-width error-banner">{error}</section> : null}
      {bankConfig.error ? <section className="card full-width error-banner">{bankConfig.error}</section> : null}

      <section className="card">
        <h2>Загрузка файла</h2>
        <div className="mode-switch">
          <button
            type="button"
            className={`mode-switch-button${form.uploadMode === 'standard' ? ' active' : ''}`}
            onClick={() => setUploadMode('standard')}
          >
            Обычная загрузка
          </button>
          <button
            type="button"
            className={`mode-switch-button${form.uploadMode === 'bank_only' ? ' active' : ''}`}
            onClick={() => setUploadMode('bank_only')}
            disabled={isTochka || bankConfig.loading || Boolean(bankConfig.error)}
          >
            Проверка для бота
          </button>
        </div>

        {form.uploadMode === 'standard' ? (
          <>
            <label>Номер проекта<input value={form.projectExternalId} onChange={(e) => setForm({ ...form, projectExternalId: e.target.value })} /></label>
            <label>Тег<input value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} /></label>
          </>
        ) : (
          <div className="muted section-note">
            В этом режиме файл проверяется банком и не отправляется в Скорозвон.
          </div>
        )}
        <label className="checkbox">
          <input type="checkbox" checked={form.hasHeader} onChange={(e) => setForm({ ...form, hasHeader: e.target.checked })} />
          В файле есть строка заголовков
        </label>
        {form.uploadMode === 'standard' && !isTochka ? (
          <label className="checkbox">
            <input type="checkbox" checked={form.bankCheckEnabled} onChange={(e) => setForm({ ...form, bankCheckEnabled: e.target.checked })} />
            Проверка контактов банком
          </label>
        ) : null}
        {isTochka ? <p className="muted">Для Точки контакты загружаются без предварительной банковской проверки. Заявка формируется после положительного результата звонка.</p> : null}

        <label
          className={`dropzone${dragActive ? ' active' : ''}${form.file ? ' has-file' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            key={form.file ? form.file.name : 'empty'}
            type="file"
            className="dropzone-input"
            onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
          />
          <div className="dropzone-title">{form.file ? 'Файл выбран' : 'Перетащите файл сюда'}</div>
          <div className="dropzone-hint">{form.file ? form.file.name : 'или нажмите, чтобы выбрать файл'}</div>
        </label>

        <button type="button" onClick={() => upload().catch((err) => setError(err.message || 'Не удалось загрузить файл'))} disabled={!form.file || uploading || bankConfig.loading || Boolean(bankConfig.error)}>
          {uploading ? 'Загрузка...' : 'Загрузить файл'}
        </button>
      </section>

      <section className="card files-list-card">
        <div className="section-header">
          <h2>Список файлов</h2>
          <div className="inline-form compact">
            <input value={filesSearch} onChange={(e) => setFilesSearch(e.target.value)} placeholder="Поиск по названию файла" />
            <button type="button" onClick={() => {
              setAppliedFilesSearch(filesSearch);
              loadFiles(1, filesSearch).catch((err) => setError(err.message || 'Не удалось выполнить поиск'));
            }}>Найти</button>
          </div>
        </div>
        {loading ? <div className="muted">Загрузка...</div> : null}
        {!loading ? <div className="muted files-summary">Всего файлов: {filesTotal}</div> : null}
        {contactsLoading ? <div className="muted">Загрузка контактов...</div> : null}
        <div className="table-wrap files-table-wrap">
          <table className="table">
            <thead>
            <tr>
              <th>ID</th>
              <th>Имя</th>
              <th>Тег</th>
              <th>Проект</th>
              <th>Дата загрузки</th>
              <th>Bank check</th>
              <th>Контактов</th>
              <th>Статус</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
              {files.map((item) => (
                  <tr key={item.id} className={selectedFileId === item.id ? 'selected-row' : ''}>
                    <td>{item.id}</td>
                    <td>{item.original_name}</td>
                    <td>{item.tag || '-'}</td>
                    <td>{item.project_external_id || '-'}</td>
                    <td>{formatDate(item.uploaded_at)}</td>
                    <td>{item.bank_check_enabled ? 'Да' : 'Нет'}</td>
                    <td>{item.contacts_count ?? 0}</td>
                    <td><span className={`status-chip status-${item.status}`}>{renderStatusLabel(item.status)}</span>
                    </td>
                    <td>
                      <div className="inline-actions">
                        <button type="button" className="action-button"
                                onClick={() => showContacts(item.id).catch((err) => setError(err.message || 'Не удалось загрузить контакты'))}>Контакты
                        </button>
                        <button
                          type="button"
                          className="action-button export-button"
                          /* disabled={item.status !== 'completed'} */
                          onClick={() => exportFile(item.id).catch((err) => setError(err.message || 'Не удалось выгрузить Excel'))}
                          title={item.status !== 'completed' ? 'Выгрузка доступна только после завершения обработки файла' : 'Выгрузить в Excel'}
                        >
                          Excel
                        </button>
                        <button type="button" className="action-button danger"
                                onClick={() => removeFile(item.id).catch((err) => setError(err.message || 'Не удалось удалить файл'))}>Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
              ))}
              {!files.length && !loading ? <tr><td colSpan="9">Файлы пока не загружены</td></tr> : null}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <div className="pagination-controls">
            <button type="button" disabled={loading || filesPage <= 1} onClick={() => loadFiles(filesPage - 1, appliedFilesSearch).catch((err) => setError(err.message || 'Не удалось загрузить страницу'))}>
              Назад
            </button>
            <span className="pagination-info">Страница {filesPage} из {filesTotalPages}</span>
            <button type="button" disabled={loading || filesPage >= filesTotalPages} onClick={() => loadFiles(filesPage + 1, appliedFilesSearch).catch((err) => setError(err.message || 'Не удалось загрузить страницу'))}>Вперёд</button>
          </div>
        </div>
      </section>

      <section className="card full-width">
        <div className="section-header">
          <h2>Контакты файла {selectedFileId ? `#${selectedFileId}` : ''}</h2>
          <div className="inline-form compact">
            <input
              value={contactsSearch}
              onChange={(e) => setContactsSearch(e.target.value)}
              placeholder="Поиск по ФИО, телефону, компании, городу"
              disabled={!selectedFileId}
            />
            <button
              type="button"
              disabled={!selectedFileId}
              onClick={() => {
                setAppliedContactsSearch(contactsSearch);
                loadContacts(selectedFileId, 1, contactsSearch, selectedStatusFilter).catch((err) => setError(err.message || 'Не удалось выполнить поиск по контактам'));
              }}
            >
              Найти
            </button>
          </div>
        </div>

        {selectedFileId ? (
          <>
            <div className="summary-grid">
              <div className="summary-card"><div className="summary-label">Всего</div><div className="summary-value">{summary?.stats?.total ?? contactsTotal}</div></div>
              <div className="summary-card"><div className="summary-label">В обработке</div><div className="summary-value">{summary?.stats?.processing ?? 0}</div></div>
              <div className="summary-card"><div className="summary-label">Заблокировано</div><div className="summary-value">{summary?.stats?.blocked ?? 0}</div></div>
              <div className="summary-card"><div className="summary-label">Проверка OK</div><div className="summary-value">{summary?.stats?.bankPassed ?? 0}</div></div>
              <div className="summary-card"><div className="summary-label">Проверка fail</div><div className="summary-value">{summary?.stats?.bankFailed ?? 0}</div></div>
              <div className="summary-card"><div className="summary-label">Отправлено в Скорозвон</div><div className="summary-value">{summary?.stats?.sentToSkorozvon ?? 0}</div></div>
            </div>

            <div className="progress-box">
              <div className="progress-head">
                <span>Прогресс файла</span>
                <span>{summary?.stats?.progressPercent ?? 0}%</span>
              </div>
              <div className="progress-track"><div className="progress-bar" style={{ width: `${summary?.stats?.progressPercent ?? 0}%` }} /></div>
              <div className="muted summary-meta">
                Статус файла: <span className={`status-chip status-${summary?.file?.status || 'unknown'}`}>{renderStatusLabel(summary?.file?.status)}</span>
                {' '}• Последняя стадия: парсинг {formatDate(summary?.file?.parse_finished_at)}, проверка {formatDate(summary?.file?.check_finished_at)}, отправка {formatDate(summary?.file?.sent_to_skorozvon_at)}
                {summaryLoading ? ' • обновление...' : ''}
              </div>
            </div>

            <div className="status-filters">
              <button type="button" className={`status-filter ${selectedStatusFilter === 'all' ? 'active' : ''}`} onClick={() => applyStatusFilter('all').catch((err) => setError(err.message || 'Не удалось применить фильтр'))}>
                Все <span>{summary?.stats?.total ?? contactsTotal}</span>
              </button>
              {statusEntries.map(([status, count]) => (
                <button key={status} type="button" className={`status-filter ${selectedStatusFilter === status ? 'active' : ''}`} onClick={() => applyStatusFilter(status).catch((err) => setError(err.message || 'Не удалось применить фильтр'))}>
                  <span className={`status-chip status-${status}`}>{renderStatusLabel(status)}</span>
                  <span>{count}</span>
                </button>
              ))}
            </div>
          </>
        ) : null}

        <div className="table-wrap">
          <table className="table">
            <thead>
            <tr>
              <th>ID</th>
              <th>ФИО</th>
              <th>Телефон</th>
              <th>Компания</th>
              <th>ИНН</th>
              <th>ОГРН</th>
              <th>Город</th>
              <th>Статус</th>
              <th>Дата</th>
            </tr>
            </thead>
            <tbody>
            {contacts.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>{item.fio || '-'}</td>
                  <td>{item.phone}</td>
                  <td>{item.company_name || '-'}</td>
                  <td>{item.inn || '-'}</td>
                  <td>{item.ogrn || '-'}</td>
                  <td>{item.city || '-'}</td>
                  <td><span className={`status-chip status-${item.status}`}>{item.status}</span></td>
                  <td>{formatDate(item.updated_at || item.created_at)}</td>
                </tr>
            ))}
            {!contacts.length && !contactsLoading ? <tr><td colSpan="9">{selectedFileId ? 'У этого файла пока нет контактов по текущему фильтру' : 'Выберите файл, чтобы посмотреть контакты'}</td></tr> : null}
            </tbody>
          </table>
        </div>

        {selectedFileId ? (
          <div className="pagination">
            <div className="pagination-controls">
              <button type="button" disabled={contactsLoading || contactsPage <= 1} onClick={() => loadContacts(selectedFileId, contactsPage - 1, appliedContactsSearch, selectedStatusFilter).catch((err) => setError(err.message || 'Не удалось загрузить страницу контактов'))}>Назад</button>
              <span className="pagination-info">Страница {contactsPage} из {contactsTotalPages}</span>
              <button type="button" disabled={contactsLoading || contactsPage >= contactsTotalPages} onClick={() => loadContacts(selectedFileId, contactsPage + 1, appliedContactsSearch, selectedStatusFilter).catch((err) => setError(err.message || 'Не удалось загрузить страницу контактов'))}>Вперёд</button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
